import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "469",
  "slug": "what-are-the-trade-winds",
  "title": "What Are the Trade Winds? Earth's Global Wind Belts Explained",
  "excerpt": "The trade winds carried sailing ships across oceans for centuries and still shape weather and deserts today. Here is what they are and how Earth's global wind belts work.",
  "date": "2026-06-17",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1683480844295-26f32c6b3fe9?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMG9jZWFuJTIwY2xvdWRzfGVufDF8MHx8fDE3ODE2ODQyMjh8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The trade winds are steady winds that blow from east to west across the tropics on either side of the equator. They are part of a global system of wind belts driven by the sun's uneven heating of Earth and the planet's rotation.",
  "content": [
    {
      "type": "paragraph",
      "text": "For centuries, sailors planned entire voyages around them. Explorers rode them west across the Atlantic, and clipper ships raced along them to carry cargo around the world. The trade winds are some of the most reliable winds on the planet, and they are a perfect window into how Earth's atmosphere works on a global scale."
    },
    {
      "type": "heading",
      "text": "What Are the Trade Winds?"
    },
    {
      "type": "paragraph",
      "text": "The trade winds are steady, prevailing winds that blow across the tropics, the band of the planet on either side of the equator. In the Northern Hemisphere they blow from the northeast, and in the Southern Hemisphere from the southeast, but in both cases they sweep generally from east to west. Their dependability is what made them so valuable to sailors, and it is also why they got their name: trade once meant a steady, regular course or track."
    },
    {
      "type": "heading",
      "text": "Why the Winds Blow"
    },
    {
      "type": "paragraph",
      "text": "Trade winds are not random. They are part of giant loops of circulating air called Hadley cells. The story goes like this: the sun heats the equator most intensely, so warm air there rises. As it climbs, it spreads toward the poles, cools, and sinks back down around the edges of the tropics. That sinking air then flows back toward the equator along the surface, and that returning flow is the trade winds."
    },
    {
      "type": "paragraph",
      "text": "But the winds do not blow in a straight north-south line. Because the Earth is spinning, moving air gets deflected, a phenomenon known as the Coriolis effect. That deflection bends the surface winds so they blow from the east rather than straight toward the equator, giving the trade winds their characteristic slant. The same effect, working in the opposite direction higher up, helps drive the winds of the mid-latitudes."
    },
    {
      "type": "heading",
      "text": "Earth's Three Big Wind Belts"
    },
    {
      "type": "paragraph",
      "text": "The trade winds are just one of three major wind belts in each hemisphere:"
    },
    {
      "type": "list",
      "items": [
        "The trade winds, blowing east to west across the tropics.",
        "The westerlies, blowing west to east across the mid-latitudes, where most of Europe and North America sit.",
        "The polar easterlies, cold winds blowing east to west near the poles."
      ]
    },
    {
      "type": "paragraph",
      "text": "Together these belts form a remarkably orderly pattern that wraps around the entire globe, the same pattern that shapes shipping routes, flight times, and weather systems to this day."
    },
    {
      "type": "heading",
      "text": "Where the Trade Winds Meet"
    },
    {
      "type": "paragraph",
      "text": "The band where the two hemispheres' trade winds converge near the equator has its own name: the Intertropical Convergence Zone, or ITCZ. As all that warm, moist air is forced upward there, it cools and dumps heavy rain, which is why the zone is marked by towering thunderclouds and feeds many of the world's tropical rainforests. The ITCZ is not fixed; it drifts north and south with the seasons, and that migration is what brings the monsoon rains to large parts of Africa and Asia each year."
    },
    {
      "type": "heading",
      "text": "The Doldrums and the Horse Latitudes"
    },
    {
      "type": "paragraph",
      "text": "Between and around these belts lie famous zones of calm. Near the equator, where the trade winds of the two hemispheres meet, air rises rather than blows sideways, creating the windless region sailors dreaded and called the doldrums. Around the edges of the tropics, where air sinks, lie the calm horse latitudes, regions of light winds, clear skies, and many of the world's great deserts."
    },
    {
      "type": "heading",
      "text": "How They Shaped History"
    },
    {
      "type": "paragraph",
      "text": "The trade winds did not just influence geography; they helped draw the map of the modern world. Reliable easterly winds in the tropics and westerlies further north created natural circular sailing routes across the Atlantic and Pacific. Whole trade networks, for better and for worse, were built around these predictable winds, and the routes they dictated still echo in today's shipping lanes."
    }
  ]
};
